import { ref, set, get, push, onValue, onChildAdded } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { authHeaders } from '../../../core/api/authHeaders';

/**
 * One peer connection per call, signalled through calls/{callId} in RTDB.
 *
 * This replaces a module-level singleton that had several call-breaking faults:
 *
 *  - The callee was rung BEFORE the caller had written the offer, and the offer write
 *    then REPLACED the whole call record. Answer quickly and there was no offer to answer.
 *    The caller now writes the record and the offer together, and only then rings.
 *  - ICE candidates that arrived before the remote description was set were "added" and
 *    the error swallowed — i.e. dropped. Every early candidate was lost, which strands
 *    calls on "connecting". They are now queued and flushed once the description lands.
 *  - Candidate lists were read with onValue, so every new candidate re-added ALL of them.
 *    Now onChildAdded: each candidate exactly once.
 *  - A voice call had no video sender, so screen sharing in a voice call always failed.
 *    Every call now negotiates a video channel up front (empty for voice), so the camera
 *    or a screen can be switched on later with replaceTrack — no renegotiation.
 *  - Only public STUN, so callers behind carrier NAT or office firewalls could not connect.
 *    TURN relay credentials now come from the proxy when a provider is configured.
 */

const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
const STUN_FALLBACK = [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }];

let iceCache = null; // { servers, relay, at }

/** ICE servers from the proxy (short-lived TURN when configured), else public STUN. */
export async function getIceServers() {
    if (iceCache && Date.now() - iceCache.at < 30 * 60 * 1000) return iceCache;
    try {
        const ctl = new AbortController();
        const timer = setTimeout(() => ctl.abort(), 4000);
        const res = await fetch(`${PROXY}/api/rtc/ice-servers`, { headers: await authHeaders(), signal: ctl.signal });
        clearTimeout(timer);
        if (res.ok) {
            const body = await res.json();
            if (Array.isArray(body?.iceServers) && body.iceServers.length) {
                iceCache = { servers: body.iceServers, relay: !!body.relay, at: Date.now() };
                return iceCache;
            }
        }
    } catch { /* fall through — a call must still be attempted */ }
    return { servers: STUN_FALLBACK, relay: false, at: Date.now() };
}

/** Camera tuned for people; screen tuned for legible text. */
async function tuneSender(sender, kind) {
    if (!sender?.getParameters) return;
    try {
        const p = sender.getParameters();
        if (!p.encodings || !p.encodings.length) p.encodings = [{}];
        if (kind === 'screen') {
            p.encodings[0].maxBitrate = 2_500_000;
            p.encodings[0].maxFramerate = 15;
            p.degradationPreference = 'maintain-resolution';
        } else {
            p.encodings[0].maxBitrate = 2_500_000;
            p.encodings[0].maxFramerate = 30;
            p.degradationPreference = 'balanced';
        }
        await sender.setParameters(p);
    } catch { /* tuning is best-effort; congestion control still adapts */ }
}

export class CallPeer {
    /**
     * @param {object} o
     * @param {string} o.callId
     * @param {'caller'|'callee'} o.role
     * @param {MediaStream} o.localStream  mic (+ camera for a video call)
     * @param {RTCIceServer[]} o.iceServers
     * @param {(s: MediaStream) => void} o.onRemoteStream   fired whenever remote tracks change state
     * @param {(state: string) => void} o.onConnectionState
     */
    constructor({ callId, role, localStream, iceServers, onRemoteStream, onConnectionState }) {
        this.callId = callId;
        this.role = role;
        this.localStream = localStream;
        this.unsubs = [];
        this.pending = [];
        this.closed = false;
        this.remoteStream = new MediaStream();
        this.onRemoteStream = onRemoteStream || (() => {});
        this.onConnectionState = onConnectionState || (() => {});

        this.pc = new RTCPeerConnection({ iceServers, iceCandidatePoolSize: 4 });
        this.pc.ontrack = (e) => {
            const t = e.track;
            if (!this.remoteStream.getTracks().includes(t)) this.remoteStream.addTrack(t);
            // Mute/unmute tells the UI when video actually starts or stops flowing
            // (a voice call's video channel is silent until someone shares a screen).
            const emit = () => this.onRemoteStream(this.remoteStream);
            t.onunmute = emit; t.onmute = emit; t.onended = emit;
            emit();
        };
        this.pc.onconnectionstatechange = () => this.onConnectionState(this.pc.connectionState);

        // The caller sets up the channels; the callee attaches to the ones the offer creates.
        if (role === 'caller') {
            localStream.getAudioTracks().forEach(t => this.pc.addTrack(t, localStream));
            const cam = localStream.getVideoTracks()[0];
            this.videoTransceiver = cam
                ? this.pc.addTransceiver(cam, { direction: 'sendrecv', streams: [localStream] })
                : this.pc.addTransceiver('video', { direction: 'sendrecv', streams: [localStream] });
        }
    }

    path(p = '') { return `calls/${this.callId}${p ? `/${p}` : ''}`; }

    async addRemoteCandidate(c) {
        if (this.closed) return;
        if (!this.pc.remoteDescription) { this.pending.push(c); return; }
        try { await this.pc.addIceCandidate(c); } catch (e) { console.warn('[call] bad candidate', e?.message); }
    }

    async flushCandidates() {
        const queued = this.pending.splice(0);
        for (const c of queued) await this.addRemoteCandidate(c);
    }

    publishCandidates(listName) {
        const list = ref(db, this.path(listName));
        this.pc.onicecandidate = (e) => { if (e.candidate) push(list, e.candidate.toJSON()).catch(() => {}); };
    }

    listenCandidates(listName) {
        this.unsubs.push(onChildAdded(ref(db, this.path(listName)), (snap) => {
            const c = snap.val();
            if (c && c.candidate !== undefined) this.addRemoteCandidate(new RTCIceCandidate(c));
        }));
    }

    /** CALLER: write the call record WITH its offer in one go, then wait for the answer. */
    async call(record) {
        this.publishCandidates('offerCandidates');
        const offer = await this.pc.createOffer();
        await this.pc.setLocalDescription(offer);
        await set(ref(db, this.path()), { ...record, offer: { type: offer.type, sdp: offer.sdp } });
        tuneSender(this.videoTransceiver?.sender, 'camera');

        this.unsubs.push(onValue(ref(db, this.path('answer')), async (snap) => {
            const ans = snap.val();
            if (!ans || this.closed || this.pc.currentRemoteDescription) return;
            try {
                await this.pc.setRemoteDescription(ans);
                await this.flushCandidates();
            } catch (e) { console.error('[call] could not apply answer', e); }
        }));
        this.listenCandidates('answerCandidates');
    }

    /** CALLEE: answer the offer already in the record. */
    async answer() {
        this.publishCandidates('answerCandidates');
        const snap = await get(ref(db, this.path('offer')));
        const offer = snap.val();
        if (!offer?.sdp) throw new Error('The call was withdrawn before it could be answered.');
        await this.pc.setRemoteDescription(offer);

        // Attach to the channels the offer created. addTrack reuses the matching
        // transceiver; the video one is made two-way even in a voice call, so either side
        // can share a screen or switch a camera on later without renegotiating.
        this.localStream.getAudioTracks().forEach(t => this.pc.addTrack(t, this.localStream));
        this.videoTransceiver = this.pc.getTransceivers().find(t => t.receiver?.track?.kind === 'video');
        const cam = this.localStream.getVideoTracks()[0];
        if (this.videoTransceiver) {
            this.videoTransceiver.direction = 'sendrecv';
            if (cam) await this.videoTransceiver.sender.replaceTrack(cam);
            try { this.videoTransceiver.sender.setStreams?.(this.localStream); } catch { /* optional API */ }
        }

        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        await set(ref(db, this.path('answer')), { type: answer.type, sdp: answer.sdp });
        await this.flushCandidates();
        tuneSender(this.videoTransceiver?.sender, 'camera');
        this.listenCandidates('offerCandidates');
    }

    /** Switch what goes out on the video channel: camera, screen, or nothing. */
    async setOutgoingVideo(track, kind = 'camera') {
        const sender = this.videoTransceiver?.sender;
        if (!sender) throw new Error('This call has no video channel.');
        if (track) track.contentHint = kind === 'screen' ? 'detail' : 'motion';
        await sender.replaceTrack(track || null);
        if (track) tuneSender(sender, kind);
    }

    async stats() {
        try { return await this.pc.getStats(); } catch { return null; }
    }

    close() {
        if (this.closed) return;
        this.closed = true;
        this.unsubs.forEach(u => { try { u(); } catch { /* ignore */ } });
        this.unsubs = [];
        this.pc.ontrack = null;
        this.pc.onicecandidate = null;
        this.pc.onconnectionstatechange = null;
        try { this.pc.close(); } catch { /* ignore */ }
    }
}

/**
 * Microphone (+ camera for video). Throws the browser's error so the caller can explain
 * it — the old code silently substituted an EMPTY stream, so a call with no microphone
 * "connected" and nobody could hear anything.
 */
export async function getCallMedia({ video }) {
    if (!navigator.mediaDevices?.getUserMedia) {
        throw Object.assign(new Error('Calls need a secure (https) page in a modern browser.'), { name: 'NotSupportedError' });
    }
    const audio = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
    if (!video) return navigator.mediaDevices.getUserMedia({ audio, video: false });
    try {
        return await navigator.mediaDevices.getUserMedia({
            audio,
            video: { width: { ideal: 1280, max: 1920 }, height: { ideal: 720, max: 1080 }, frameRate: { ideal: 30 }, facingMode: 'user' },
        });
    } catch (e) {
        // A camera problem should not stop a call: fall back to voice and say so.
        if (['NotFoundError', 'NotReadableError', 'OverconstrainedError', 'AbortError'].includes(e?.name)) {
            const s = await navigator.mediaDevices.getUserMedia({ audio, video: false });
            s.cameraFallback = e;
            return s;
        }
        throw e;
    }
}

export async function getCameraTrack() {
    const s = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280, max: 1920 }, height: { ideal: 720, max: 1080 }, frameRate: { ideal: 30 }, facingMode: 'user' },
    });
    return s.getVideoTracks()[0];
}

export async function getScreenTrack() {
    if (!navigator.mediaDevices?.getDisplayMedia) {
        throw Object.assign(new Error('Screen sharing is not available in this browser.'), { name: 'NotSupportedError' });
    }
    const s = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 15, max: 30 } }, audio: false });
    return s.getVideoTracks()[0];
}
